import styled from "@emotion/styled";
import { ChannelPageProps } from "./ChannelPageProps";
import { FeatureContainer, kafSystemWidth, systemWidth } from "@mediaspace/shared/styled";
import {
    ChannelHeaderType,
    ChannelLayoutMode,
    ChannelSection,
    ChannelViewMode,
    EventPlatformEntry,
    SectionIndex,
    WebcastEntry,
} from "@mediaspace/shared/types";
import {
    SyntheticEvent,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { translate } from "@mediaspace/shared/utils";
import Sessions from "./sessions/Sessions";
import PlaylistCarousels from "./playlist-carousels/PlaylistCarousels";
import AboutSection from "./about-section/AboutSection";
import ChannelHeader from "./channel-header/ChannelHeader";
import MediaGrid from "@mediaspace/shared/media-grid";
import MediaList from "./media-section/list/MediaList";
import { Tab, Tabs, Toast, Typography } from "@kaltura/ds-react-components";
import TabPanel from "@mediaspace/shared/ui/tabs/TabPanel";
import ActionsMenu from "./actions-menu/ActionsMenu";
import BoardMessages from "./board-messages/BoardMessages";
import { tabProps } from "@mediaspace/shared/ui";
import { Snackbar } from "@mui/material";
import { useButtonAnalytics } from "@mediaspace/hooks";
import { ButtonClickAnalyticsType } from "@mediaspace/shared/types/ButtonClickAnalyticsType";
import { ConfigContext } from "@mediaspace/shared/data-kms-config";
import LeanChannelHeader from "./channel-header/lean-channel-header/LeanChannelHeader";
import { NotificationsManager } from "@mediaspace/shared/channel-tv";
import sortBy from "lodash/sortBy";

export const indexSection = {
    "1": translate("Sessions"),
    "2": translate("Media"),
    "3": translate("Playlists"),
    "4": translate("Learn More"),
};

const DEFAULT_EXTENDED_TAB_COMPONENTS_INDEX = 1000;

const StyledChannelContent = styled("div", {
    shouldForwardProp(propName) {
        return propName !== "isKaf";
    },
})<{ isKaf?: boolean }>(({ theme, isKaf }) => ({
    "&": isKaf ? kafSystemWidth({ theme }) : systemWidth({ theme }),
}));

const StyledActions = styled("div", {
    shouldForwardProp(propName: PropertyKey): boolean {
        return propName !== "isVerticalView";
    },
})<{ isVerticalView: boolean }>(({ theme, isVerticalView }) => ({
    position: "relative",
    zIndex: 1,
    textAlign: "right",
    ...(!isVerticalView && {
        marginLeft: "auto",
        paddingTop: theme.spacing(1.5),
    }),
}));

const StyledSection = styled("div")(({ theme }) => ({
    marginTop: theme.spacing(7.5),
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        marginTop: theme.spacing(2),
    },
}));

const StyledSectionTitle = styled(Typography)(
    ({ theme }) => ({
        marginTop: theme.spacing(0),
        marginBottom: theme.spacing(3),
    })
);

const StyledTabbedSection = styled("div")(({ theme }) => ({
    marginTop: theme.spacing(3),
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        marginTop: theme.spacing(2),
    },
}));

/**
 * Channel Page
 */
const ChannelPage = (props: ChannelPageProps) => {
    const {
        entries = [],
        about = false,
        playlists = [],
        hasMorePlaylists,
        description,
        categoryId,
        media,
        actions,
        channelViewMode = ChannelViewMode.Grid,
        channelLayoutMode = ChannelLayoutMode.Tabs,
        defaultSection = SectionIndex.SESSIONS,
        showSpeakersForContentSectionEntries = false,
        boardMessages = [],
        name,
        channelHeaderType = ChannelHeaderType.Show_Banner,
        enableChannelTvNotifications = false,
        notificationTimeoutMS
    } = props;

    const [activeSection, setActiveSection] = useState(
        indexSection[defaultSection]
    );
    const [showToast, setShowToast] = useState<boolean>(false);
    const [severity, setSeverity] = useState<"success" | "error">("success");
    const [toastMessage, setToastMessage] = useState<string>("");

    const readMoreSectionEl = useRef(null);

    const isVerticalView =
        channelLayoutMode === ChannelLayoutMode.Vertical ||
        channelLayoutMode === ChannelLayoutMode.Fixed_Vertical;

    const isLeanHeader =
        channelHeaderType === ChannelHeaderType.Hide_Banner ||
        channelHeaderType === ChannelHeaderType.Fixed_Hide_Banner;

    // content width is different in case of kaf
    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;

    const getSections = useCallback(
        (
            channelViewMode: string,
            showSpeakersForContentsectionEntries: boolean,
            webcasts: WebcastEntry[] = [],
            videos: EventPlatformEntry[] = [],
            playlists: {
                entries: EventPlatformEntry;
                id: string;
                name: string;
            }[] = [],
            hasMorePlaylists,
            about = "",
            additionalSections: ChannelSection[] = []
        ): ChannelSection[] => {
            const sections: ChannelSection[] = [];
            if (webcasts.length > 0) {
                sections.push({
                    name: translate("Sessions"),
                    content: webcasts,
                    component: Sessions,
                    index: SectionIndex.SESSIONS,
                    additionalParams: {
                        hideFilterSessionsButton: isVerticalView,
                        analyticsValue: "Sessions",
                    },
                });
            }
            if (videos.length > 0) {
                channelViewMode === ChannelViewMode.Grid
                    ? sections.push({
                          name: translate("Media"),
                          content: videos,
                          component: MediaGrid,
                          index: SectionIndex.MEDIA,
                          additionalParams: {
                              analyticsValue: "Media - Media Thumbnail",
                              showPagination: !isVerticalView,
                          },
                      })
                    : sections.push({
                          name: translate("Media"),
                          content: videos,
                          component: MediaList,
                          index: SectionIndex.MEDIA,
                          additionalParams: {
                              showSpeakersForContentsectionEntries:
                                  showSpeakersForContentsectionEntries,
                              analyticsValue: "Media",
                          },
                      });
            }
            if (playlists.length > 0) {
                sections.push({
                    name: translate("Playlists"),
                    content: playlists,
                    component: PlaylistCarousels,
                    index: SectionIndex.PLAYLISTS,
                    additionalParams: {
                        analyticsValue: "Playlist",
                        hasMorePlaylists: hasMorePlaylists,
                    },
                });
            }
            if (about) {
                sections.push({
                    name: translate("Learn More"),
                    content: about,
                    component: AboutSection,
                    index: SectionIndex.ABOUT,
                    additionalParams: {
                        analyticsValue: "Learn More",
                    },
                });
            }

            additionalSections.forEach((section)=>{
                sections.push({
                    ...section,
                    name: section.name,
                    index: section.index || DEFAULT_EXTENDED_TAB_COMPONENTS_INDEX,
                });
            });

            return sections;
        },
        [isVerticalView]
    );

    // filter out the media that shows on the channel header
    const entriesToShow = useMemo(
        () =>
            entries.filter((entry) => {
                return !media || entry.id !== media.entryId;
            }),
        [entries, media]
    );

    // extract "sessions" list
    const webcasts = useMemo(
        () =>
            entriesToShow.length > 0
                ? entriesToShow.filter((entry) => {
                      return (
                          entry.schedulingData !== undefined &&
                          entry.schedulingData !== null
                      );
                  })
                : [],
        [entriesToShow]
    );

    const sortedSessions = useMemo(
        () =>
            enableChannelTvNotifications
                ? sortBy(
                    webcasts,
                    (session) => session.schedulingData.start.timestamp
                )
                : [],
        [enableChannelTvNotifications, webcasts]
    );

    // extract "media" list
    const videos = useMemo(
        () =>
            entriesToShow.length > 0
                ? entriesToShow.filter((entry) => {
                      return (
                          entry.schedulingData === undefined ||
                          entry.schedulingData === null
                      );
                  })
                : [],
        [entriesToShow]
    );

    const sections = useMemo(() => {
        const sections = getSections(
            channelViewMode,
            showSpeakersForContentSectionEntries,
            webcasts as WebcastEntry[],
            videos as EventPlatformEntry[],
            playlists,
            hasMorePlaylists,
            about ? description : "",
            props.additionalSections
        );
        // support no sections
        if (sections.length === 0) {
            return [];
        }
        // set defaultSection as first item in sections array, if exists
        const firstSection =
            sections.find(
                (section) =>
                    section.name.toLowerCase() ===
                    indexSection[defaultSection].toLowerCase()
            ) || sections[0];
        const filteredSections = sections.filter(
            (section) =>
                section.name.toLowerCase() !== firstSection.name.toLowerCase()
        );
        return [firstSection, ...filteredSections];
    }, [
        getSections,
        channelViewMode,
        showSpeakersForContentSectionEntries,
        webcasts,
        videos,
        playlists,
        hasMorePlaylists,
        about,
        description,
    ]);

    const handleActiveTab = (event: SyntheticEvent, newValue: string) => {
        setActiveSection(newValue);
    };

    const onReadMore = (() => {
        if (!about) {
            return;
        }
        return () => {
            if (isVerticalView) {
                readMoreSectionEl?.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            } else {
                setActiveSection(indexSection[SectionIndex.ABOUT]);
            }
        };
    })();

    const onShowNotification = (
        message: string,
        severity: "success" | "error"
    ) => {
        setSeverity(severity);
        setShowToast(true);
        setToastMessage(message);
    };

    useEffect(() => {
        // set initial section:
        // if the default exists use that, otherwise get the first section.
        // if there is no section available (ie no entries, no playlists, no description),
        // use undefined - the page will show empty, but won't crash.
        const section = sections.find(
            (section) => section.index === defaultSection
        );
        setActiveSection(section?.name ?? sections[0]?.name);
    }, [defaultSection, sections]);

    const sendButtonAnalytics = useButtonAnalytics();
    const styledActions = (actions && actions.length > 0) ? (<StyledActions isVerticalView={isVerticalView}>
        <ActionsMenu
            actions={actions}
            onShowNotification={onShowNotification}
        />
    </StyledActions>) : null;

    return (
        <FeatureContainer className={"kms-ds-channel-page"} isKaf={isKaf}>
            {isLeanHeader && <LeanChannelHeader {...props} />}
            {!isLeanHeader && <ChannelHeader {...props} onReadMore={onReadMore} />}
            <StyledChannelContent
                isKaf={isKaf}
                className={"kms-ds-channel-page-body"}
            >
                {boardMessages && (
                    <BoardMessages
                        boardMessages={boardMessages}
                        channelName={name}
                        channelId={categoryId}
                    />
                )}
                {isVerticalView && styledActions}
                {!isVerticalView && (
                    <>
                        <Tabs value={activeSection} onChange={handleActiveTab}>
                            {sections.map((section) => {
                                return (
                                    <Tab
                                        {...tabProps(section.name)}
                                        label={section.name}
                                        key={`tab-${section.name}`}
                                        onClick={() => {
                                            sendButtonAnalytics(
                                                `Channel Tab - ${section.additionalParams.analyticsValue}`,
                                                ButtonClickAnalyticsType.BROWSE
                                            );
                                        }}
                                    />
                                );
                            })}
                            {styledActions}
                        </Tabs>

                        {sections.map((section) => {
                            return (
                                <TabPanel
                                    currentTab={activeSection}
                                    name={section.name}
                                    key={`section-${section.name}`}
                                >
                                    <StyledTabbedSection
                                        className={
                                            "kms-ds-channel-page-section-tabs"
                                        }
                                    >
                                        <section.component
                                            isComponentInView={activeSection === section.name} // used originally for PS components
                                            content={section.content}
                                            categoryId={categoryId}
                                            {...section.additionalParams}
                                        />
                                    </StyledTabbedSection>
                                </TabPanel>
                            );
                        })}
                    </>
                )}

                {isVerticalView && (
                    <>
                        {sections.map((section) => {
                            return (
                                <StyledSection
                                    id={`section-${section.index}`}
                                    key={`section-${section.index}`}
                                    className={`kms-ds-channel-page-vertical-section-wrapper`}
                                    ref={
                                        section.component.displayName ===
                                        "AboutSection"
                                            ? readMoreSectionEl
                                            : null
                                    }
                                >
                                    { section.component !== PlaylistCarousels ? (
                                        <StyledSectionTitle
                                            component={"h3"}
                                            variant={"h3"}
                                            className={`kms-ds-channel-page-vertical-section-title`}
                                        >
                                            {section.name}
                                        </StyledSectionTitle>
                                    ) : null }
                                    <section.component
                                        isComponentInView={true} // used originally for PS components
                                        content={section.content}
                                        categoryId={categoryId}
                                        {...section.additionalParams}
                                    />
                                </StyledSection>
                            );
                        })}
                    </>
                )}
            </StyledChannelContent>

            <Snackbar
                open={showToast}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Toast onClose={() => setShowToast(false)} severity={severity}>
                    <span>{toastMessage}</span>
                </Toast>
            </Snackbar>

            {enableChannelTvNotifications && <NotificationsManager
                sessions={sortedSessions as WebcastEntry[]}
                notificationTimeoutMS={notificationTimeoutMS}
                channelId={categoryId}></NotificationsManager>}
        </FeatureContainer>
    );
};

export default ChannelPage;
